/**
 * Deploy-time runtime config injected via `window.__CONFIG__` (see
 * `public/config.js` — the placeholder ships `window.__CONFIG__ = {};` and
 * `scripts/deploy.sh` regenerates it at deploy time with the real values, per
 * the static-web-build-artifact skill).
 *
 * The four auth/API fields are REQUIRED at runtime: without all four the app
 * must render an explicit configuration-error screen rather than attempt a
 * sign-in with a partial configuration (ISSUE-329 §Cross-service JSON
 * contract). `buildVersion` / `deployedAt` are optional so the app still runs
 * against the un-deployed placeholder during local development.
 *
 * None of these values is a secret — a public PKCE client's id, authority,
 * scope and API base URL are all discoverable from the shipped bundle by
 * design (ADR-055 §7). No secret is ever read here or shipped to the client.
 */

/** Fields the app cannot start without. */
export interface RequiredConfig {
  /** MSAL SPA public-client application id (`WEB_ADMIN_ENTRA_CLIENT_ID`). */
  entraClientId: string;
  /** MSAL authority URL for the workforce tenant (`WEB_ADMIN_ENTRA_AUTHORITY`). */
  entraAuthority: string;
  /** Scope requested for the admin-API access token (`WEB_ADMIN_ENTRA_API_SCOPE`). */
  entraApiScope: string;
  /** Admin-API base URL (`WEB_ADMIN_API_BASE_URL`). */
  apiBaseUrl: string;
}

/** Provenance fields the deploy script stamps in; absent in local dev. */
export interface BuildInfo {
  /** The deployed artifact's ADO build number (`SERVICE_VERSION`), e.g. "0.1.7". */
  buildVersion?: string;
  /** Human-readable UTC deploy timestamp, e.g. "2026-09-02 14:03:11 UTC". */
  deployedAt?: string;
}

export type RuntimeConfig = RequiredConfig & BuildInfo;

/** Every required key, in the order the configuration-error screen lists them. */
export const REQUIRED_CONFIG_KEYS = [
  "entraClientId",
  "entraAuthority",
  "entraApiScope",
  "apiBaseUrl",
] as const satisfies readonly (keyof RequiredConfig)[];

export type ConfigResult =
  | { readonly ok: true; readonly config: RuntimeConfig }
  | { readonly ok: false; readonly missing: readonly (keyof RequiredConfig)[] };

declare global {
  interface Window {
    /** Untyped at the global boundary on purpose — narrowed only by readConfig(). */
    __CONFIG__?: unknown;
  }
}

function readString(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function readRaw(): Record<string, unknown> {
  const raw = window.__CONFIG__;
  if (typeof raw !== "object" || raw === null) {
    return {};
  }
  // reason: the typeof/null check above narrows `raw` to a non-null object;
  // indexing it by string key still needs this cast, but every field read off
  // it is re-validated by readString() before it is trusted.
  return raw as Record<string, unknown>;
}

/** Provenance only — always safe to read, even when the app is misconfigured. */
export function getBuildInfo(): BuildInfo {
  const raw = readRaw();
  const buildVersion = readString(raw.buildVersion);
  const deployedAt = readString(raw.deployedAt);
  const info: BuildInfo = {};
  if (buildVersion !== undefined) {
    info.buildVersion = buildVersion;
  }
  if (deployedAt !== undefined) {
    info.deployedAt = deployedAt;
  }
  return info;
}

/**
 * The one typed accessor for `window.__CONFIG__`. Every consumer reads config
 * through this function — never scatter the global read across components.
 * Returns the missing key list instead of throwing so the app can render a
 * configuration-error screen naming exactly what the deploy failed to inject.
 */
export function readConfig(): ConfigResult {
  const raw = readRaw();
  const values: Partial<RequiredConfig> = {};
  const missing: (keyof RequiredConfig)[] = [];
  for (const key of REQUIRED_CONFIG_KEYS) {
    const value = readString(raw[key]);
    if (value === undefined) {
      missing.push(key);
    } else {
      values[key] = value;
    }
  }
  if (missing.length > 0) {
    return { ok: false, missing };
  }
  return {
    ok: true,
    config: { ...(values as RequiredConfig), ...getBuildInfo() },
  };
}
